/**
 * User Profile API
 * Handles user settings, preferences, and subscription tiers
 */

import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('UserProfile');

// Subscription tier types
export type UserTier = 'free' | 'basic' | 'pro' | 'enterprise' | 'admin';
export type BillingCycle = 'monthly' | 'yearly';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'none';

// Bandwidth limit behavior - what happens when user exceeds their limit
export type BandwidthLimitBehavior = 'soft' | 'hard';
// 'soft' = Allow overages, bill at end of month
// 'hard' = Block tours from loading when limit is exceeded

// Feature keys for access control
export type FeatureKey =
  | 'uploads'
  | 'analytics'
  | 'full_analytics'
  | 'white_label'
  | 'priority_support'
  | 'sla'
  | 'self_host_export'  // Export tours as standalone packages for self-hosting
  | 'ai_generation'     // AI 360 image generation and editing
  | 'lead_capture';     // Lead capture forms in hotspots

// Tier configuration with limits and features
export interface TierConfig {
  name: string;
  displayName: string;
  price: { monthly: number; yearly: number } | 'custom';
  hostedExperienceLimit: number; // Cloud-hosted experiences, -1 for unlimited (deprecated - not enforced)
  selfHostedExperienceLimit: number; // Self-hosted experiences, -1 for unlimited
  storageLimit: number; // in bytes, -1 for unlimited (cloud storage)
  muxMinutesLimit: number; // Total Mux video minutes allowed, -1 for unlimited, 0 for none
  aiProLimit: number; // AI Pro model generations per month, -1 for unlimited, 0 for none
  aiFlashLimit: number; // AI Flash model generations per month, -1 for unlimited, 0 for none
  bandwidthLimit: number; // Bytes per month, -1 for unlimited, 0 for no hosted content
  bandwidthOverageRate: number; // Cents per GB overage (for future billing)
  features: FeatureKey[];
  description: string;
  targetAudience: string;
  highlighted?: boolean; // For UI to show "Most Popular" badge
}

// Centralized tier configuration
export const TIER_CONFIG: Record<UserTier, TierConfig> = {
  free: {
    name: 'free',
    displayName: 'Free',
    price: { monthly: 0, yearly: 0 },
    hostedExperienceLimit: -1, // Unlimited (limited by storage)
    selfHostedExperienceLimit: -1, // Unlimited self-hosted
    storageLimit: 0, // No cloud storage
    muxMinutesLimit: 0, // No Mux video uploads
    aiProLimit: 0, // No AI access
    aiFlashLimit: 0,
    bandwidthLimit: 0, // No hosted content
    bandwidthOverageRate: 0,
    features: ['analytics', 'self_host_export'],
    description: 'Self-hosted with all features. Perfect for developers.',
    targetAudience: 'Developers, students',
  },
  basic: {
    name: 'basic',
    displayName: 'Basic',
    price: { monthly: 9, yearly: 90 }, // ~$7.50/mo yearly
    hostedExperienceLimit: -1, // Unlimited (limited by storage)
    selfHostedExperienceLimit: -1, // Unlimited self-hosted
    storageLimit: 5 * 1024 * 1024 * 1024, // 5GB cloud storage
    muxMinutesLimit: 60, // 60 minutes (1 hour) of Mux video uploads
    aiProLimit: 0, // No AI access
    aiFlashLimit: 0,
    bandwidthLimit: 100 * 1024 * 1024 * 1024, // 100GB per month
    bandwidthOverageRate: 10, // $0.10 per GB overage
    features: ['uploads', 'analytics', 'priority_support', 'self_host_export', 'lead_capture'],
    description: '5GB cloud storage with HLS video streaming.',
    targetAudience: 'Small businesses',
    highlighted: true,
  },
  pro: {
    name: 'pro',
    displayName: 'Professional',
    price: { monthly: 29, yearly: 290 }, // ~$24/mo yearly
    hostedExperienceLimit: -1, // Unlimited (limited by storage)
    selfHostedExperienceLimit: -1, // Unlimited self-hosted
    storageLimit: 50 * 1024 * 1024 * 1024, // 50GB cloud storage
    muxMinutesLimit: 300, // 300 minutes (5 hours) of Mux video uploads
    aiProLimit: 20, // 20 AI Pro generations per month
    aiFlashLimit: 100, // 100 AI Flash generations per month
    bandwidthLimit: 1024 * 1024 * 1024 * 1024, // 1TB per month
    bandwidthOverageRate: 10, // $0.10 per GB overage
    features: ['uploads', 'analytics', 'full_analytics', 'white_label', 'priority_support', 'self_host_export', 'ai_generation', 'lead_capture'],
    description: '50GB storage, AI generation & editing, white-label branding.',
    targetAudience: 'Agencies, galleries',
  },
  enterprise: {
    name: 'enterprise',
    displayName: 'Enterprise',
    price: 'custom',
    hostedExperienceLimit: -1, // Unlimited
    selfHostedExperienceLimit: -1, // Unlimited self-hosted
    storageLimit: -1, // Unlimited cloud storage
    muxMinutesLimit: -1, // Unlimited Mux video uploads
    aiProLimit: -1, // Unlimited AI
    aiFlashLimit: -1,
    bandwidthLimit: -1, // Unlimited
    bandwidthOverageRate: 0, // Custom contract
    features: ['uploads', 'analytics', 'full_analytics', 'white_label', 'priority_support', 'sla', 'self_host_export', 'ai_generation', 'lead_capture'],
    description: 'Unlimited storage, AI, and SLA guarantee.',
    targetAudience: 'Museums, corporations',
  },
  admin: {
    name: 'admin',
    displayName: 'Admin',
    price: { monthly: 0, yearly: 0 },
    hostedExperienceLimit: -1,
    selfHostedExperienceLimit: -1,
    storageLimit: -1,
    muxMinutesLimit: -1,
    aiProLimit: -1, // Unlimited AI
    aiFlashLimit: -1,
    bandwidthLimit: -1, // Unlimited
    bandwidthOverageRate: 0,
    features: ['uploads', 'analytics', 'full_analytics', 'white_label', 'priority_support', 'sla', 'self_host_export', 'ai_generation', 'lead_capture'],
    description: 'Full administrative access.',
    targetAudience: 'Internal',
  },
};

// Feature display names for UI
export const FEATURE_DISPLAY_NAMES: Record<FeatureKey, { name: string; description: string }> = {
  uploads: { name: 'Media Uploads', description: 'Upload images, videos, and 3D models' },
  analytics: { name: 'Basic Analytics', description: 'View count and basic engagement metrics' },
  full_analytics: { name: 'Advanced Analytics', description: 'Detailed heatmaps, session recordings, and export' },
  white_label: { name: 'White Label', description: 'Remove XRGallery branding from experiences' },
  priority_support: { name: 'Priority Support', description: '24-hour response time guarantee' },
  sla: { name: 'SLA', description: '99.9% uptime guarantee with credits' },
  self_host_export: { name: 'Self-Host Export', description: 'Download tours as standalone packages to host on your own servers' },
  ai_generation: { name: 'AI Generation', description: 'Generate and edit 360° images with AI (Pro: 20 + Flash: 100/mo)' },
  lead_capture: { name: 'Lead Capture Forms', description: 'Embed contact forms and email signups in your virtual tours' },
};

export interface UserProfile {
  userId: string;
  email?: string;
  displayName?: string;
  username?: string | null; // Optional username for URL prefix (e.g., xrgallery.online/username/tour)
  tier: UserTier; // User's subscription tier
  analyticsEnabled: boolean; // Account-level analytics toggle
  storageUsed?: number; // Bytes used (for quota enforcement)
  muxMinutesUsed?: number; // Total Mux video minutes uploaded (lifetime)
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // Subscription fields
  subscriptionStatus?: SubscriptionStatus;
  billingCycle?: BillingCycle;
  subscriptionStartDate?: Timestamp;
  subscriptionEndDate?: Timestamp;
  stripeCustomerId?: string; // For Stripe integration
  stripeSubscriptionId?: string;
  // Bandwidth tracking
  bandwidthUsed?: number; // Bytes used this billing cycle
  bandwidthCycleStart?: Timestamp; // Start of current billing cycle
  bandwidthLastUpdated?: Timestamp; // Last time bandwidth was recorded
  bandwidthLimitBehavior?: BandwidthLimitBehavior; // 'soft' (overages) or 'hard' (block), default 'soft'
  bandwidthWarningsSent?: number[]; // Thresholds at which warnings were sent (e.g., [80, 100])
  bandwidthOverageReported?: number; // Bytes of overage already reported to Stripe
  // Note: Bandwidth overages are reported to Stripe via Billing Meters using stripeCustomerId
  // BYOK (Bring Your Own Key) - Personal Gemini API key
  // Note: The actual API key is stored securely in /users/{userId}/private/geminiKey
  // This flag indicates whether the user has a personal key configured
  hasPersonalGeminiKey?: boolean;
}

/**
 * Get user profile by user ID
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data() as UserProfile;
    } else {
      logger.debug('Profile not found for user:', userId);
      return null;
    }
  } catch (error) {
    logger.error('Error getting user profile:', error);
    throw new Error('Failed to get user profile');
  }
}

/**
 * Create user profile (called on first login/signup)
 */
export async function createUserProfile(
  userId: string,
  email?: string,
  displayName?: string
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const now = Timestamp.now();

    const profileData: UserProfile = {
      userId,
      email: email || '',
      displayName: displayName || '',
      tier: 'free', // Default to free tier
      analyticsEnabled: true, // Default to enabled
      storageUsed: 0, // Start with 0 bytes used
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(userRef, profileData);
    logger.info('Profile created for user:', userId);
  } catch (error) {
    logger.error('Error creating user profile:', error);
    throw new Error('Failed to create user profile');
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<UserProfile, 'userId' | 'createdAt'>>
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);

    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(userRef, updateData);
    logger.debug('Profile updated for user:', userId);
  } catch (error) {
    logger.error('Error updating user profile:', error);
    throw new Error('Failed to update user profile');
  }
}

/**
 * Toggle analytics for user account
 */
export async function toggleUserAnalytics(
  userId: string,
  enabled: boolean
): Promise<void> {
  try {
    await updateUserProfile(userId, { analyticsEnabled: enabled });
    logger.info(`Analytics ${enabled ? 'enabled' : 'disabled'} for user:`, userId);
  } catch (error) {
    logger.error('Error toggling analytics:', error);
    throw error;
  }
}

/**
 * Get or create user profile (ensure profile exists with all required fields)
 */
export async function ensureUserProfile(
  userId: string,
  email?: string,
  displayName?: string
): Promise<UserProfile> {
  try {
    let profile = await getUserProfile(userId);

    if (!profile) {
      // Create profile if it doesn't exist
      await createUserProfile(userId, email, displayName);
      profile = await getUserProfile(userId);

      if (!profile) {
        throw new Error('Failed to create user profile');
      }
    } else {
      // Backfill missing fields for existing users (migration support)
      let needsUpdate = false;
      const updates: Partial<UserProfile> = {};

      if (profile.tier === undefined) {
        updates.tier = 'free';
        needsUpdate = true;
        logger.debug(`Backfilling tier for user: ${userId}`);
      }

      if (profile.storageUsed === undefined) {
        updates.storageUsed = 0;
        needsUpdate = true;
        logger.debug(`Backfilling storageUsed for user: ${userId}`);
      }

      if (needsUpdate) {
        await updateUserProfile(userId, updates);
        // Reload profile with updated fields
        profile = await getUserProfile(userId);

        if (!profile) {
          throw new Error('Failed to reload user profile after update');
        }

        logger.info(`User profile updated with missing fields: ${userId}`);
      }
    }

    return profile;
  } catch (error) {
    logger.error('Error ensuring user profile:', error);
    throw error;
  }
}

/**
 * Update user tier (admin function for manual tier management)
 */
export async function updateUserTier(
  userId: string,
  tier: UserTier
): Promise<void> {
  try {
    await updateUserProfile(userId, { tier });
    logger.info(`Tier updated to "${tier}" for user:`, userId);
  } catch (error) {
    logger.error('Error updating tier:', error);
    throw error;
  }
}

/**
 * Check if user has access to a feature based on tier
 */
export function hasFeatureAccess(profile: UserProfile, feature: FeatureKey): boolean {
  const tierConfig = TIER_CONFIG[profile.tier];
  if (!tierConfig) return false;
  return tierConfig.features.includes(feature);
}

/**
 * Get the cloud-hosted experience limit for a user's tier
 * Returns -1 for unlimited, 0 for none
 */
export function getHostedExperienceLimit(profile: UserProfile): number {
  const tierConfig = TIER_CONFIG[profile.tier];
  return tierConfig?.hostedExperienceLimit ?? 0;
}

/**
 * Get the self-hosted experience limit for a user's tier
 * Returns -1 for unlimited
 */
export function getSelfHostedExperienceLimit(profile: UserProfile): number {
  const tierConfig = TIER_CONFIG[profile.tier];
  return tierConfig?.selfHostedExperienceLimit ?? 0;
}

/**
 * Get the storage limit for a user's tier in bytes
 * Returns -1 for unlimited, 0 for none
 */
export function getStorageLimit(profile: UserProfile): number {
  const tierConfig = TIER_CONFIG[profile.tier];
  return tierConfig?.storageLimit ?? 0;
}

/**
 * Check if user can create more cloud-hosted experiences
 */
export function canCreateHostedExperience(profile: UserProfile, currentHostedCount: number): boolean {
  const limit = getHostedExperienceLimit(profile);
  if (limit === -1) return true; // Unlimited
  if (limit === 0) return false; // No cloud hosting allowed
  return currentHostedCount < limit;
}

/**
 * Check if user can create more self-hosted experiences
 * (Currently always true for all tiers, but structure allows future limits)
 */
export function canCreateSelfHostedExperience(profile: UserProfile, currentSelfHostedCount: number): boolean {
  const limit = getSelfHostedExperienceLimit(profile);
  if (limit === -1) return true; // Unlimited
  return currentSelfHostedCount < limit;
}

/**
 * Legacy function for backwards compatibility - checks hosted limit
 * @deprecated Use canCreateHostedExperience or canCreateSelfHostedExperience instead
 */
export function canCreateExperience(profile: UserProfile, currentCount: number): boolean {
  return canCreateHostedExperience(profile, currentCount);
}

/**
 * Check if user has enough storage remaining
 */
export function hasStorageAvailable(profile: UserProfile, bytesNeeded: number): boolean {
  const limit = getStorageLimit(profile);
  if (limit === -1) return true; // Unlimited
  const used = profile.storageUsed ?? 0;
  return (used + bytesNeeded) <= limit;
}

/**
 * Get tier display information
 */
export function getTierDisplayInfo(tier: UserTier): TierConfig {
  return TIER_CONFIG[tier];
}

/**
 * Compare tiers (returns positive if tier1 > tier2)
 */
export function compareTiers(tier1: UserTier, tier2: UserTier): number {
  const tierOrder: UserTier[] = ['free', 'basic', 'pro', 'enterprise', 'admin'];
  return tierOrder.indexOf(tier1) - tierOrder.indexOf(tier2);
}

/**
 * Check if user can upgrade to a specific tier
 */
export function canUpgradeTo(currentTier: UserTier, targetTier: UserTier): boolean {
  if (currentTier === 'admin') return false; // Admin can't upgrade
  if (targetTier === 'admin') return false; // Can't upgrade to admin
  return compareTiers(targetTier, currentTier) > 0;
}

/**
 * Update storage usage (for future quota tracking)
 */
export async function updateStorageUsage(
  userId: string,
  bytesUsed: number
): Promise<void> {
  try {
    await updateUserProfile(userId, { storageUsed: bytesUsed });
    logger.debug(`Storage updated to ${bytesUsed} bytes for user:`, userId);
  } catch (error) {
    logger.error('Error updating storage:', error);
    throw error;
  }
}

/**
 * Update bandwidth limit behavior ('soft' = allow overages, 'hard' = block when exceeded)
 */
export async function updateBandwidthLimitBehavior(
  userId: string,
  behavior: BandwidthLimitBehavior
): Promise<void> {
  try {
    await updateUserProfile(userId, { bandwidthLimitBehavior: behavior });
    logger.info(`Bandwidth limit behavior set to '${behavior}' for user:`, userId);
  } catch (error) {
    logger.error('Error updating bandwidth limit behavior:', error);
    throw error;
  }
}

// ============================================================================
// MUX VIDEO UPLOAD LIMITS
// ============================================================================

/**
 * Get the Mux video minutes limit for a user's tier
 * Returns -1 for unlimited, 0 for none
 */
export function getMuxMinutesLimit(profile: UserProfile): number {
  const tierConfig = TIER_CONFIG[profile.tier];
  return tierConfig?.muxMinutesLimit ?? 0;
}

/**
 * Get the Mux video minutes used by a user
 */
export function getMuxMinutesUsed(profile: UserProfile): number {
  return profile.muxMinutesUsed ?? 0;
}

/**
 * Get the remaining Mux video minutes for a user
 * Returns -1 if unlimited
 */
export function getMuxMinutesRemaining(profile: UserProfile): number {
  const limit = getMuxMinutesLimit(profile);
  if (limit === -1) return -1; // Unlimited
  if (limit === 0) return 0; // No uploads allowed
  const used = getMuxMinutesUsed(profile);
  return Math.max(0, limit - used);
}

/**
 * Check if user can upload Mux videos (has remaining minutes)
 */
export function canUploadMuxVideo(profile: UserProfile): boolean {
  const limit = getMuxMinutesLimit(profile);
  if (limit === -1) return true; // Unlimited
  if (limit === 0) return false; // No uploads allowed
  return getMuxMinutesUsed(profile) < limit;
}

/**
 * Format Mux minutes for display (e.g., "45 min" or "2 hr 30 min")
 */
export function formatMuxMinutes(minutes: number): string {
  if (minutes === -1) return 'Unlimited';
  if (minutes === 0) return '0 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
}

